import type { TbcPoint } from './tbc-types';
import type { ClusterSpec, PopcornSpec } from '@kinnet-studio/pattern';

/** A freshly placed cluster: a 3-member double-crochet bobble worked into one
 *  stitch (挑針). */
export const DEFAULT_CLUSTER: ClusterSpec = {
  memberType: 'double-crochet',
  count: 3,
  into: 'stitch',
};

/** 挑針/挑束 bow: base step + per-crossbar widening, as a fraction of leg length.
 *  More crossbars (tr=2, dtr=3) need a wider bow so the slashes don't tangle. */
export const CLUSTER_BASE_BOW = 0.18;
export const CLUSTER_PER_CROSSBAR_BOW = 0.09;
/** 挑束 open-base spread (fraction of leg length). 挑針 uses 0. */
export const CLUSTER_SPACE_BASE_SPREAD = 0.15;

export function bowStepForCrossbars(crossbarCount: number): number {
  return CLUSTER_BASE_BOW + CLUSTER_PER_CROSSBAR_BOW * crossbarCount;
}

/** Unit axis (base→top), its +90° perpendicular, and the leg length. For a
 *  zero-length leg, dir defaults to +y and perp to (−1,0) (finite downstream). */
export function clusterAxis(base: TbcPoint, top: TbcPoint): { u: TbcPoint; perp: TbcPoint; len: number } {
  const dx = top.x - base.x;
  const dy = top.y - base.y;
  const len = Math.hypot(dx, dy);
  const ux = len === 0 ? 0 : dx / len;
  const uy = len === 0 ? 1 : dy / len;
  return { u: { x: ux, y: uy }, perp: { x: -uy, y: ux }, len };
}

/** Quadratic-Bézier spine for member `i` of a `count`-member cluster.
 *  `baseSpread` (fraction of leg length) opens the base for 挑束 (0 = 挑針,
 *  single base point). All members share `top`. `bowStep` sets the lateral
 *  bulge of the arc; the middle member of an odd count is straight. */
export function clusterMemberSpine(
  base: TbcPoint, top: TbcPoint, count: number, i: number,
  baseSpread: number, bowStep: number,
): { p0: TbcPoint; c: TbcPoint; p1: TbcPoint } {
  const { perp, len } = clusterAxis(base, top);
  const u = count <= 1 ? 0 : (i - (count - 1) / 2) / (count - 1); // [-0.5, 0.5]
  const p0: TbcPoint = { x: base.x + perp.x * u * baseSpread * len, y: base.y + perp.y * u * baseSpread * len };
  const lat = i - (count - 1) / 2; // raw offset drives bow magnitude
  const mid = { x: (p0.x + top.x) / 2, y: (p0.y + top.y) / 2 };
  const c: TbcPoint = { x: mid.x + perp.x * lat * bowStep * len, y: mid.y + perp.y * lat * bowStep * len };
  return { p0, c, p1: top };
}

/** Point on a quadratic Bézier at parameter s ∈ [0,1]. */
export function quadPoint(p0: TbcPoint, c: TbcPoint, p1: TbcPoint, s: number): TbcPoint {
  const m = 1 - s;
  const a = m * m, b = 2 * m * s, d = s * s;
  return { x: a * p0.x + b * c.x + d * p1.x, y: a * p0.y + b * c.y + d * p1.y };
}

/** Unit tangent of a quadratic Bézier at s (derivative direction). Falls back to
 *  the chord direction when the derivative is degenerate. */
export function quadTangent(p0: TbcPoint, c: TbcPoint, p1: TbcPoint, s: number): TbcPoint {
  const dx = 2 * (1 - s) * (c.x - p0.x) + 2 * s * (p1.x - c.x);
  const dy = 2 * (1 - s) * (c.y - p0.y) + 2 * s * (p1.y - c.y);
  const m = Math.hypot(dx, dy);
  if (m === 0) {
    const cx = p1.x - p0.x, cy = p1.y - p0.y;
    const cm = Math.hypot(cx, cy) || 1;
    return { x: cx / cm, y: cy / cm };
  }
  return { x: dx / m, y: dy / m };
}

/** Sample a quadratic Bézier into `samples`+1 points (inclusive of both ends). */
export function quadPolyline(p0: TbcPoint, c: TbcPoint, p1: TbcPoint, samples: number): TbcPoint[] {
  const out: TbcPoint[] = [];
  for (let k = 0; k <= samples; k++) out.push(quadPoint(p0, c, p1, k / samples));
  return out;
}

/** A freshly placed popcorn: a 5-member double-crochet popcorn into one stitch. */
export const DEFAULT_POPCORN: PopcornSpec = {
  memberType: 'double-crochet',
  count: 5,
  into: 'stitch',
};

/** The gather descriptor for a cluster OR popcorn (structurally identical), or
 *  undefined for any other stitch. Single accessor so the shared geometry/
 *  sizing code serves both types. */
export function gatherSpecOf(t: { cluster?: ClusterSpec; popcorn?: PopcornSpec }): ClusterSpec | PopcornSpec | undefined {
  return t.cluster ?? t.popcorn;
}
